import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "@/lib/utils";
import { gridSpan } from "@/components/ui/form-field";

/**
 * Replacement for Bootstrap's `custom-control custom-switch`, which the
 * dashboard forms use for every on/off setting and which disappears with
 * Bootstrap. Radix gives it the keyboard and ARIA behaviour the hand-rolled
 * checkbox never had: it reports `role="switch"` with `aria-checked`, and
 * toggles on both Space and Enter.
 */
const Switch = React.forwardRef(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full",
      "border-2 border-transparent transition-colors duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        "pointer-events-none block size-5 rounded-full bg-background shadow-sm ring-0",
        "transition-transform duration-150",
        "data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

/**
 * A switch with its label, sized to sit on a `FormGrid` row alongside regular
 * fields: `span` means the same thing it does on `Field`.
 */
const SwitchField = ({ id, label, span = 12, className, ...props }) => (
  <div className={cn("col-span-12 flex items-center gap-3", gridSpan(span), className)}>
    <Switch id={id} {...props} />
    <label htmlFor={id} className="cursor-pointer text-sm text-foreground">
      {label}
    </label>
  </div>
);

export { Switch, SwitchField };
